import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Palette, Save, RotateCcw, Download, Upload, Sparkles, Type as TypeIcon, Ruler, Sliders, Shield } from "lucide-react";
import { toast } from "sonner";
import { THEME_PRESETS, FONT_OPTIONS, DENSITY_PRESETS, DEFAULT_THEME, resolveTheme, applyThemeTo } from "../lib/theme";

const COLOR_TOKENS = [
  ["--bg", "Background"],
  ["--surface", "Surface"],
  ["--surface-2", "Surface 2 (raised)"],
  ["--border", "Border"],
  ["--muted", "Muted"],
  ["--text", "Text"],
  ["--text-muted", "Text muted"],
  ["--brand", "Brand"],
  ["--brand-fg", "Brand foreground"],
  ["--accent", "Accent"],
  ["--success", "Success"],
  ["--danger", "Danger"],
];
const TABS = ["palette", "presets", "typography", "shape", "import"];

export default function ThemeStudio() {
  const { settings, refreshSettings } = useSettings();
  const [tab, setTab] = useState("presets");
  const [theme, setTheme] = useState(() => resolveTheme(settings?.theme));
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);
  const [previewPath, setPreviewPath] = useState("/dashboard");

  useEffect(() => { setTheme(resolveTheme(settings?.theme)); }, [settings?.theme]);

  const set = (key, value) => setTheme((t) => ({ ...t, [key]: value }));
  const setMany = (patch) => setTheme((t) => ({ ...t, ...patch }));

  // Live-inject the theme into the preview iframe (same-origin, so we can access its DOM).
  useEffect(() => {
    const applyToIframe = () => {
      const doc = iframeRef.current?.contentDocument;
      if (doc?.documentElement) applyThemeTo(doc.documentElement, theme);
    };
    applyToIframe();
    const t = setInterval(applyToIframe, 800); // keep re-applying in case iframe swaps route
    return () => clearInterval(t);
  }, [theme, previewPath]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { theme });
      await refreshSettings();
      toast.success("Theme saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const revert = () => { setTheme(resolveTheme(settings?.theme)); toast.info("Reverted to saved theme"); };
  const resetDefault = () => { setTheme({ ...DEFAULT_THEME }); toast.info("Reset to Gold on Black default (not saved yet)"); };

  const exportJson = useMemo(() => JSON.stringify(theme, null, 2), [theme]);

  return (
    <AppLayout>
      <div className="mb-6 flex items-center gap-3">
        <Shield className="w-4 h-4 text-[color:var(--brand)]" />
        <div className="font-mono-num text-[11px] text-[color:var(--brand)] uppercase tracking-[0.3em]">// Admin Console</div>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl">Theme Studio</h1>
          <p className="text-[color:var(--text-muted)] mt-2 text-sm max-w-xl">Palette, typography, shape and density — with a live preview of the whole app. Changes apply everywhere including the public landing page once you save.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={revert} className="border-[color:var(--border)] bg-transparent hover:bg-[color:var(--surface-2)] rounded-none text-[color:var(--text-muted)]" data-testid="theme-revert"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Revert</Button>
          <Button variant="outline" onClick={resetDefault} className="border-[color:var(--border)] bg-transparent hover:bg-[color:var(--surface-2)] rounded-none text-[color:var(--text-muted)]" data-testid="theme-reset"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Reset to default</Button>
          <Button onClick={save} disabled={saving} className="rounded-none uppercase tracking-widest font-semibold h-10 px-5" style={{ backgroundColor: "var(--brand)", color: "var(--brand-fg)" }} data-testid="theme-save"><Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save Theme"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* Editor */}
        <Card className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-none p-0" data-testid="theme-editor">
          <div className="flex border-b border-[color:var(--border)] overflow-x-auto">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-4 py-3 font-mono-num text-[10px] uppercase tracking-widest whitespace-nowrap border-r border-[color:var(--border)] ${tab===t?"bg-[color:var(--surface-2)] text-[color:var(--brand)]":"text-[color:var(--text-muted)] hover:text-[color:var(--text)]"}`}
                data-testid={`theme-tab-${t}`}>{t}</button>
            ))}
          </div>

          <div className="p-5 space-y-4">
            {tab === "palette" && (
              <div className="space-y-3">
                {COLOR_TOKENS.map(([k, label]) => (
                  <div key={k} className="grid grid-cols-[1fr_auto_120px] items-center gap-2">
                    <Label className="text-[11px] uppercase tracking-widest font-mono-num text-[color:var(--text-muted)]">{label}</Label>
                    <input type="color" value={theme[k] || "#000000"} onChange={(e) => set(k, e.target.value)} className="h-9 w-12 bg-transparent border border-[color:var(--border)] cursor-pointer" data-testid={`color-${k.replace(/^--/, '')}`} />
                    <Input value={theme[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-[color:var(--surface-2)] border-[color:var(--border)] rounded-none h-9 font-mono-num text-xs uppercase" />
                  </div>
                ))}
              </div>
            )}

            {tab === "presets" && (
              <div className="grid grid-cols-1 gap-2">
                {THEME_PRESETS.map((p) => (
                  <button key={p.id} onClick={() => setMany(p.tokens)} className="text-left border border-[color:var(--border)] hover:border-[color:var(--brand)] bg-[color:var(--surface-2)] p-3 flex items-center gap-3 transition-colors" data-testid={`preset-${p.id}`}>
                    <div className="flex gap-1 shrink-0">
                      {p.swatch.map((c, i) => <div key={i} className="w-6 h-10" style={{ background: c }} />)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-black uppercase tracking-tight text-sm">{p.name}</div>
                      <div className="text-[11px] text-[color:var(--text-muted)] leading-relaxed">{p.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {tab === "typography" && (
              <div className="space-y-4">
                {[["--font-display", "Display font", FONT_OPTIONS.display], ["--font-body", "Body font", FONT_OPTIONS.body], ["--font-mono", "Mono font", FONT_OPTIONS.mono]].map(([k, label, opts]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-widest font-mono-num text-[color:var(--text-muted)]">{label}</Label>
                    <select value={theme[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full h-10 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded-none px-3 text-sm" data-testid={`font-${k.replace(/^--/, '')}`}>
                      {opts.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
                    </select>
                    <div className="text-lg mt-1" style={{ fontFamily: theme[k] }}>The quick brown fox jumps over the lazy dog</div>
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-widest font-mono-num text-[color:var(--text-muted)]">Font scale ({theme["--font-scale"] || "1"})</Label>
                  <input type="range" min="0.85" max="1.2" step="0.05" value={parseFloat(theme["--font-scale"]) || 1} onChange={(e) => set("--font-scale", e.target.value)} className="w-full" data-testid="font-scale" />
                </div>
              </div>
            )}

            {tab === "shape" && (
              <div className="space-y-4">
                {[["--radius-sm", "Small radius"], ["--radius-md", "Medium radius"], ["--radius-lg", "Large radius"]].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-widest font-mono-num text-[color:var(--text-muted)]">{label} ({theme[k] || "0px"})</Label>
                    <input type="range" min="0" max="24" step="1" value={parseInt(theme[k]) || 0} onChange={(e) => set(k, `${e.target.value}px`)} className="w-full" data-testid={`shape-${k.replace(/^--/, '')}`} />
                    <div className="w-16 h-10 border border-[color:var(--border)] bg-[color:var(--surface-2)]" style={{ borderRadius: theme[k] }} />
                  </div>
                ))}
                <div>
                  <Label className="text-[11px] uppercase tracking-widest font-mono-num text-[color:var(--text-muted)] mb-2 block">Density</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {DENSITY_PRESETS.map((d) => (
                      <button key={d.id} onClick={() => setMany(d.tokens)} className="border border-[color:var(--border)] hover:border-[color:var(--brand)] bg-[color:var(--surface-2)] py-2 text-[11px] uppercase tracking-widest font-mono-num" data-testid={`density-${d.id}`}>{d.label}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === "import" && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { navigator.clipboard.writeText(exportJson); toast.success("Copied to clipboard"); }} className="border-[color:var(--border)] bg-transparent rounded-none text-[color:var(--text)]" data-testid="theme-export"><Download className="w-3.5 h-3.5 mr-1.5" /> Copy JSON</Button>
                  <Button variant="outline" onClick={() => {
                    const el = document.getElementById("theme-import-ta");
                    try { const j = JSON.parse(el.value); setTheme(resolveTheme(j)); toast.success("Theme imported"); }
                    catch { toast.error("Invalid JSON"); }
                  }} className="border-[color:var(--border)] bg-transparent rounded-none text-[color:var(--text)]" data-testid="theme-import"><Upload className="w-3.5 h-3.5 mr-1.5" /> Import from textarea</Button>
                </div>
                <Textarea id="theme-import-ta" rows={16} defaultValue={exportJson} key={exportJson} className="bg-[color:var(--surface-2)] border-[color:var(--border)] rounded-none font-mono-num text-xs" data-testid="theme-json-ta" />
                <p className="text-[11px] text-[color:var(--text-muted)]">Paste a theme JSON blob to overwrite, or copy the current tokens to back up. Save afterwards to persist.</p>
              </div>
            )}
          </div>
        </Card>

        {/* Preview iframe */}
        <Card className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-none p-0 overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[color:var(--border)] px-3 py-2">
            <div className="flex gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex gap-1 ml-2 flex-wrap">
              {["/dashboard", "/library", "/community", "/playbook", "/login"].map((p) => (
                <button key={p} onClick={() => setPreviewPath(p)} className={`px-2 py-1 text-[10px] font-mono-num uppercase tracking-widest border ${previewPath===p?"border-[color:var(--brand)] text-[color:var(--brand)]":"border-[color:var(--border)] text-[color:var(--text-muted)]"}`} data-testid={`preview-${p.slice(1)}`}>{p}</button>
              ))}
            </div>
          </div>
          <iframe
            ref={iframeRef}
            src={previewPath}
            title="Live theme preview"
            className="w-full"
            style={{ height: "720px", background: "var(--bg)" }}
            data-testid="theme-preview-iframe"
          />
        </Card>
      </div>
    </AppLayout>
  );
}
